import React, { useCallback, useEffect, useRef, useState } from 'react';

import { Group } from '@lumapps/groups/types';
import { isLearningAlphaEnabled } from '@lumapps/learning/ducks/selectors';
import { ServiceNotAvailableState } from '@lumapps/lumx-states/components/ServiceNotAvailableState';
import { Button, Emphasis, FlexBox, List, ListItem, ListSubheader } from '@lumapps/lumx/react';
import { useSelector } from '@lumapps/redux/react';
import { GLOBAL, useTranslate } from '@lumapps/translations';
import { BaseLoadingStatus, isError, isLoading } from '@lumapps/utils/types/BaseLoadingStatus';

import { getLeaderboard } from '../../api';
import { DEFAULT_MAX_NUMBER_ELEMENTS_ON_LEADERBOARD_LIST, SIZE_INITIAL_RANKING_LIST } from '../../constants';
import { LEADERBOARD } from '../../keys';
import { RankingData, userTypes } from '../../types';
import { RankingListItem } from '../RankingListItem';
import { RankingListItemSkeleton } from '../RankingListItem/RankingListItemSkeleton';

export interface LeaderboardProps {
    groups?: Group[];
}
/**
 *  Leaderboad component that will display user ranking on the learning feature
 * @param LeaderboardProps
 * @returns Leaderboard
 */

export const Leaderboard: React.FC<LeaderboardProps> = ({ groups }) => {
    const groupId = groups?.map((group) => group.id);
    const groupsRef = useRef(groups);
    const [currentUserData, setCurrentUserData] = useState<RankingData>();
    const [rankingList, setRankingList] = useState<RankingData[]>([]);
    const [hasMore, setHasMore] = useState<boolean>(true);
    const learningAlphaEnabled = useSelector(isLearningAlphaEnabled);

    const [status, setStatus] = useState(BaseLoadingStatus.initial);
    const { translateKey } = useTranslate();
    const offset = 0;
    const limit = SIZE_INITIAL_RANKING_LIST;

    const fetchLeaderboardData = useCallback(
        async (offset = 0) => {
            try {
                setStatus(BaseLoadingStatus.loading);

                const response = await getLeaderboard(offset, limit, learningAlphaEnabled, groupId);
                if (offset === 0) {
                    setRankingList(response.rankingList?.items || []);
                } else {
                    setRankingList((prevList) => [...prevList, ...(response.rankingList?.items || [])]);
                }
                setHasMore(response.rankingList.more);
                if (response?.currentUser) {
                    setCurrentUserData(response.currentUser);
                }
                setStatus(BaseLoadingStatus.idle);
            } catch (e) {
                setStatus(BaseLoadingStatus.error);
            }
        },
        [groupId, learningAlphaEnabled, limit],
    );

    useEffect(() => {
        if (status === BaseLoadingStatus.initial || groupsRef.current !== groups) {
            fetchLeaderboardData();
            groupsRef.current = groups;
        }
    }, [fetchLeaderboardData, status, groups]);

    const handleLoadMore = () => {
        fetchLeaderboardData(rankingList.length || 0);
    };
    const checkUserType = (ranking: RankingData) => {
        if (rankingList && rankingList?.length > 1) {
            if (currentUserData?.identifier === ranking.identifier) {
                return userTypes.currentUserInTotalRanking;
            }
            return userTypes.someUser;
        }
        return userTypes.currentUserRanking;
    };

    const shouldShowAverageScore = (rankingList: RankingData[], index: number) => {
        const currentPoints = rankingList[index]?.totalPoints;
        const nextPoints = rankingList[index + 1]?.totalPoints;
        const prevPoints = rankingList[index - 1]?.totalPoints;

        if (
            (currentPoints !== undefined && currentPoints === nextPoints) ||
            (currentPoints !== undefined && currentPoints === prevPoints)
        ) {
            return true;
        }
        return false;
    };
    return (
        <FlexBox className="lumx-spacing-padding-huge">
            {isError(status) && (
                <ServiceNotAvailableState
                    shouldShowIcon
                    onRetry={() => getLeaderboard(offset, limit, learningAlphaEnabled)}
                />
            )}

            {isLoading(status) && (
                <>
                    <List>
                        <ListSubheader> {translateKey(LEADERBOARD.USER_RANKING)}</ListSubheader>
                        <ListItem>
                            <RankingListItemSkeleton />
                        </ListItem>
                    </List>
                    <List>
                        <ListSubheader> {translateKey(LEADERBOARD.FULL_RANKING)}</ListSubheader>
                        {Array.from({ length: DEFAULT_MAX_NUMBER_ELEMENTS_ON_LEADERBOARD_LIST }, () => {
                            return (
                                <ListItem>
                                    <RankingListItemSkeleton />
                                </ListItem>
                            );
                        })}
                    </List>
                </>
            )}

            {currentUserData && (
                <>
                    <List>
                        <ListSubheader> {translateKey(LEADERBOARD.USER_RANKING)}</ListSubheader>
                        <ListItem>
                            <RankingListItem
                                key={currentUserData.identifier}
                                ranking={currentUserData}
                                userType={userTypes.currentUserRanking}
                            />
                        </ListItem>
                    </List>
                    {rankingList.length > 0 && (
                        <List>
                            <ListSubheader> {translateKey(LEADERBOARD.FULL_RANKING)}</ListSubheader>

                            {rankingList?.map((ranking, index) => {
                                const showAverageScore = shouldShowAverageScore(rankingList, index);
                                const userType = checkUserType(ranking);
                                return (
                                    <ListItem key={ranking.identifier}>
                                        <RankingListItem
                                            key={ranking?.ranking}
                                            ranking={ranking}
                                            userType={userType}
                                            showAverageScore={showAverageScore}
                                        />
                                    </ListItem>
                                );
                            })}
                        </List>
                    )}

                    {rankingList.length > 0 && hasMore && (
                        <Button fullWidth emphasis={Emphasis.medium} onClick={handleLoadMore}>
                            {translateKey(GLOBAL.NEXT)}
                        </Button>
                    )}
                </>
            )}
        </FlexBox>
    );
};
